import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import {
  Users, FileWarning, MessageSquare, Star, Wrench, Car, Trash2, Shield,
  CheckCircle2, XCircle, Pause, Play, Eye, Clock, ScrollText, BadgeCheck, Search, Crown, Gift, CreditCard, UserX, TrendingUp, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShopReviewSheet } from "@/components/ShopReviewSheet";

export default function AdminDashboard() {
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [issues, setIssues] = useState([]);
  const [pendingShops, setPendingShops] = useState([]);
  const [audit, setAudit] = useState([]);
  const [subscriptions, setSubscriptions] = useState(null);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null); // {action, user}
  const [reviewShopId, setReviewShopId] = useState(null);

  const load = async () => {
    try {
      const [s, u, i, p, a, subs] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/issues"),
        api.get("/admin/shops/pending"),
        api.get("/admin/audit-log"),
        api.get("/admin/shops/subscriptions"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setIssues(i.data);
      setPendingShops(p.data);
      setAudit(a.data);
      setSubscriptions(subs.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deleteUser = async (uid) => {
    if (!window.confirm("Delete this user? Their data remains but they cannot log in.")) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success("User removed"); load(); }
    catch { toast.error("Failed"); }
  };

  const impersonate = async (uid) => {
    try {
      const target = await startImpersonation(uid);
      toast.success(`Acting as ${target.shop_name || target.name}`);
      if (target.role === "shop") navigate("/shop");
      else navigate("/owner");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleTier = async (u) => {
    const order = ["free", "pro", "premium"];
    const idx = order.indexOf(u.subscription_tier || "free");
    const next = order[(idx + 1) % order.length];
    try {
      await api.post(`/admin/shops/${u.user_id}/tier`, { tier: next });
      toast.success(`Set ${u.email} → ${next}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="min-h-screen bg-[#09090B]"><Navbar /><div className="p-12 text-zinc-500">Loading...</div></div>;

  const tabs = [
    { k: "pending", label: "PENDING SHOPS", icon: Clock, count: pendingShops.length, accent: pendingShops.length > 0 },
    { k: "subscriptions", label: "SUBSCRIPTIONS", icon: Crown, count: subscriptions?.totals?.approved_shops || 0 },
    { k: "users", label: "USERS", icon: Users, count: users.length },
    { k: "issues", label: "ISSUES", icon: FileWarning, count: issues.length },
    { k: "audit", label: "AUDIT LOG", icon: ScrollText, count: audit.length },
  ];

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="admin-dashboard">
        <div className="flex items-center gap-3 mb-3">
          <Shield className="w-5 h-5 text-[#FF3B30]" />
          <div className="label-eyebrow">ADMIN CONSOLE</div>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-10">Operations.</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-6">
          <Stat label="USERS" value={stats?.users} icon={Users} />
          <Stat label="OWNERS" value={stats?.owners} icon={Car} />
          <Stat label="APPROVED SHOPS" value={(stats?.shops ?? 0) - (stats?.pending_shops ?? 0)} icon={Wrench} />
          <Stat label="PENDING SHOPS" value={stats?.pending_shops} accent={stats?.pending_shops > 0} icon={Clock} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
          <Stat label="ISSUES" value={stats?.issues} icon={FileWarning} />
          <Stat label="OPEN" value={stats?.open_issues} />
          <Stat label="QUOTES" value={stats?.bids} />
          <Stat label="REVIEWS" value={stats?.reviews} icon={Star} />
        </div>

        <div className="flex flex-wrap border border-white/10 mb-6 w-fit">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.k;
            return (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-widest font-medium transition-colors ${active ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}
                data-testid={`tab-${t.k}`}>
                <Icon className="w-3.5 h-3.5" /> {t.label}
                {t.count > 0 && <span className={`text-[10px] font-mono ${t.accent && !active ? "text-[#FF3B30]" : "opacity-70"}`}>({t.count})</span>}
              </button>
            );
          })}
        </div>

        {tab === "pending" && <PendingTab shops={pendingShops} onReview={(s) => setReviewShopId(s.user_id)} onAction={(a, s) => setActionDialog({ action: a, user: s })} />}
        {tab === "subscriptions" && <SubscriptionsTab data={subscriptions} onImpersonate={impersonate} />}
        {tab === "users" && <UsersTable users={users} onDelete={deleteUser} onImpersonate={impersonate} onAction={(a, u) => setActionDialog({ action: a, user: u })} onToggleTier={toggleTier} />}
        {tab === "issues" && <IssuesTable issues={issues} />}
        {tab === "audit" && <AuditTable rows={audit} />}

        <ShopActionDialog
          state={actionDialog}
          onClose={() => setActionDialog(null)}
          onSuccess={() => { setActionDialog(null); setReviewShopId(null); load(); }}
        />

        <ShopReviewSheet
          shopUserId={reviewShopId}
          open={!!reviewShopId}
          onOpenChange={(o) => !o && setReviewShopId(null)}
          onAction={(a, s) => setActionDialog({ action: a, user: s })}
        />
      </main>
    </div>
  );
}

function Stat({ label, value, accent, icon: Icon }) {
  return (
    <div className="bg-[#09090B] p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="label-eyebrow">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-zinc-600" strokeWidth={1.5} />}
      </div>
      <div className={`font-heading text-4xl font-black ${accent ? "text-[#FF3B30]" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function PendingTab({ shops, onReview, onAction }) {
  if (shops.length === 0) {
    return <div className="border border-dashed border-white/10 p-12 text-center text-zinc-500" data-testid="pending-empty">No shops waiting for approval. 🎉</div>;
  }
  const ready = shops.filter((s) => s.application);
  const awaiting = shops.filter((s) => !s.application);
  return (
    <div className="space-y-8">
      {ready.length > 0 && (
        <div>
          <div className="label-eyebrow mb-3 flex items-center gap-2">
            <span className="text-[#34C759]">●</span> READY TO REVIEW · {ready.length}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ready.map((s) => (
              <div key={s.user_id} className="border border-white/10 bg-[#121214] p-6 flex flex-col" data-testid={`pending-${s.user_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-[#FF9500] flex items-center justify-center">
                    <Clock className="w-5 h-5 text-black" strokeWidth={2.5} />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest bg-[#34C759] text-black px-2 py-1">SUBMITTED</span>
                </div>
                <h3 className="font-heading text-xl font-bold tracking-tight mb-1">{s.application?.legal_business_name || s.shop_name}</h3>
                <p className="text-sm text-zinc-400 mb-1">{s.application?.owner_full_name || s.name}</p>
                <p className="text-xs text-zinc-500 font-mono mb-1 truncate">{s.email}</p>
                {s.account_number && <p className="text-[10px] uppercase tracking-widest text-zinc-600 mb-3" data-testid={`acct-${s.user_id}`}>{s.account_number}</p>}
                <div className="text-xs text-zinc-400 mb-4 space-y-1">
                  {s.application?.is_mobile ? (
                    <div>📍 Mobile · {s.application?.service_radius_miles} mi radius</div>
                  ) : (
                    <div>📍 {s.application?.address?.city}, {s.application?.address?.state}</div>
                  )}
                  <div>🔧 {(s.application?.service_categories || []).slice(0, 3).join(", ")}{(s.application?.service_categories?.length || 0) > 3 ? "…" : ""}</div>
                  <div>📅 {s.application?.years_experience} yrs experience</div>
                </div>
                <div className="mt-auto flex gap-2">
                  <button onClick={() => onReview(s)} className="btn-primary flex items-center gap-1.5 text-xs py-2 flex-1 justify-center" data-testid={`review-${s.user_id}`}>
                    Review application
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {awaiting.length > 0 && (
        <div>
          <div className="label-eyebrow mb-3 flex items-center gap-2">
            <span className="text-[#FF9500]">●</span> AWAITING APPLICATION · {awaiting.length}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {awaiting.map((s) => (
              <div key={s.user_id} className="border border-white/10 bg-[#121214] p-6 opacity-70" data-testid={`pending-${s.user_id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-zinc-800 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-zinc-500" strokeWidth={2.5} />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest bg-[#FF9500] text-black px-2 py-1">NO APP</span>
                </div>
                <h3 className="font-heading text-xl font-bold tracking-tight mb-1">{s.shop_name || "Unnamed Shop"}</h3>
                <p className="text-sm text-zinc-400 mb-1">{s.name}</p>
                <p className="text-xs text-zinc-500 font-mono mb-1 truncate">{s.email}</p>
                {s.account_number && <p className="text-[10px] uppercase tracking-widest text-zinc-600 mb-3" data-testid={`acct-${s.user_id}`}>{s.account_number}</p>}
                <p className="text-xs text-zinc-500 italic">Cannot approve until shop submits their application.</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UsersTable({ users, onDelete, onImpersonate, onAction, onToggleTier }) {
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const filtered = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      (u.account_number || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.shop_name || "").toLowerCase().includes(q) ||
      (u.user_id || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="border border-white/10 bg-[#121214] p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by account # (MC-000123), email, name, or shop..."
            className="input-base pl-10"
            data-testid="admin-user-search"
          />
        </div>
        <div className="flex items-center gap-1 border border-white/10 divide-x divide-white/10">
          {["all", "owner", "shop", "admin"].map((r) => (
            <button
              key={r}
              onClick={() => setRoleFilter(r)}
              className={`px-3 py-2 text-[10px] uppercase tracking-widest transition-colors ${roleFilter === r ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:bg-white/5"}`}
              data-testid={`admin-role-filter-${r}`}
            >{r}</button>
          ))}
        </div>
        <div className="text-xs text-zinc-500 font-mono whitespace-nowrap" data-testid="admin-user-count">{filtered.length} / {users.length}</div>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#121214]">
            <tr className="text-left">
              <Th>ACCT #</Th><Th>EMAIL</Th><Th>NAME</Th><Th>ROLE</Th><Th>PLAN</Th><Th>STATUS</Th><Th>SHOP</Th><Th>RATING</Th><Th>ACTIONS</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-500" data-testid="admin-users-empty">No matching accounts.</td></tr>
            ) : filtered.map((u) => (
              <tr key={u.user_id} className="border-t border-white/10 hover:bg-white/5" data-testid={`user-row-${u.user_id}`}>
                <Td className="font-mono text-xs tracking-widest text-zinc-300">{u.account_number || "—"}</Td>
                <Td className="font-mono text-xs">{u.email}</Td>
                <Td>{u.name}</Td>
                <Td><RoleBadge role={u.role} /></Td>
                <Td>
                  {u.role === "shop" ? (() => {
                    const t = u.subscription_tier || "free";
                    const styles = t === "premium"
                      ? "bg-[#FF3B30] text-white"
                      : t === "pro"
                        ? "bg-[#FF9500] text-white"
                        : "border border-white/10 text-zinc-500";
                    const label = t === "premium" ? "Premium" : t === "pro" ? "Pro" : "Free";
                    return (
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${styles}`} data-testid={`tier-${u.user_id}`}>
                        {label}
                      </span>
                    );
                  })() : <span className="text-zinc-700">—</span>}
                </Td>
                <Td><StatusBadge status={u.status} /></Td>
                <Td>{u.shop_name || "—"}</Td>
                <Td>{u.rating_avg ? `${u.rating_avg} (${u.rating_count})` : "—"}</Td>
                <Td>
                  <div className="flex items-center gap-2">
                    {u.role !== "admin" && (
                      <button onClick={() => onImpersonate(u.user_id)} className="text-zinc-500 hover:text-[#007AFF]" title="Impersonate" data-testid={`impersonate-${u.user_id}`}>
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    {u.role === "shop" && (
                      <button onClick={() => onToggleTier(u)} className={`hover:text-[#FF3B30] ${u.subscription_tier === "premium" ? "text-[#FF3B30]" : u.subscription_tier === "pro" ? "text-[#FF9500]" : "text-zinc-500"}`} title="Cycle tier (Free → Pro → Premium)" data-testid={`toggle-tier-${u.user_id}`}>
                        <Crown className="w-4 h-4" />
                      </button>
                    )}
                    {u.role === "shop" && u.status === "approved" && (
                      <button onClick={() => onAction("suspend", u)} className="text-zinc-500 hover:text-[#FF9500]" title="Suspend" data-testid={`suspend-${u.user_id}`}>
                        <Pause className="w-4 h-4" />
                      </button>
                    )}
                    {u.role === "shop" && (u.status === "suspended" || u.status === "rejected") && (
                      <button onClick={() => onAction("reinstate", u)} className="text-zinc-500 hover:text-[#34C759]" title="Reinstate" data-testid={`reinstate-${u.user_id}`}>
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                    {u.role !== "admin" && (
                      <button onClick={() => onDelete(u.user_id)} className="text-zinc-500 hover:text-[#FF3B30]" title="Delete" data-testid={`delete-user-${u.user_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IssuesTable({ issues }) {
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#121214]">
          <tr className="text-left">
            <Th>TITLE</Th><Th>VEHICLE</Th><Th>STATUS</Th><Th>CATEGORY</Th><Th>CREATED</Th>
          </tr>
        </thead>
        <tbody>
          {issues.map((i) => (
            <tr key={i.issue_id} className="border-t border-white/10 hover:bg-white/5">
              <Td className="max-w-xs truncate">{i.title}</Td>
              <Td>{[i.vehicle_make, i.vehicle_model, i.vehicle_year].filter(Boolean).join(" ") || "—"}</Td>
              <Td><span className="text-[10px] uppercase tracking-widest px-2 py-1 border border-white/10">{i.status}</span></Td>
              <Td>{i.ai_category || "—"}</Td>
              <Td className="font-mono text-xs text-zinc-500">{new Date(i.created_at).toLocaleDateString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTable({ rows }) {
  if (rows.length === 0) return <div className="border border-dashed border-white/10 p-12 text-center text-zinc-500">No admin actions recorded yet.</div>;
  const actionColor = {
    impersonate_start: "bg-[#007AFF] text-white",
    shop_approve: "bg-[#34C759] text-black",
    shop_reject: "bg-[#FF3B30] text-white",
    shop_suspend: "bg-[#FF9500] text-black",
    shop_reinstate: "bg-[#34C759] text-black",
  };
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#121214]">
          <tr className="text-left">
            <Th>WHEN</Th><Th>ADMIN</Th><Th>ACTION</Th><Th>TARGET</Th><Th>DETAILS</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-white/10 hover:bg-white/5" data-testid={`audit-${r.id}`}>
              <Td className="font-mono text-xs text-zinc-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</Td>
              <Td className="font-mono text-xs">{r.admin_email || r.admin_id}</Td>
              <Td><span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${actionColor[r.action] || "border border-white/10"}`}>{r.action}</span></Td>
              <Td className="font-mono text-xs text-zinc-500">{r.target_user_id || "—"}</Td>
              <Td className="text-xs text-zinc-400">{JSON.stringify(r.meta || {})}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShopActionDialog({ state, onClose, onSuccess }) {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  if (!state) return null;
  const { action, user } = state;
  const titles = {
    approve: { title: "Approve shop", body: "Welcome them aboard. They'll receive an email immediately.", confirmText: "Approve", placeholder: "Optional welcome note", needsReason: false },
    reject: { title: "Reject application", body: "We'll email this shop and let them know.", confirmText: "Reject", placeholder: "Reason (optional, included in the email)", needsReason: false },
    suspend: { title: "Suspend shop", body: "They keep their account but cannot send quotes or messages while suspended.", confirmText: "Suspend", placeholder: "Reason (optional, included in the email)", needsReason: false },
    reinstate: { title: "Reinstate shop", body: "They will be approved again immediately.", confirmText: "Reinstate", placeholder: "", needsReason: false },
  }[action];

  const submit = async () => {
    setLoading(true);
    try {
      if (action === "approve") await api.post(`/admin/shops/${user.user_id}/approve`, { note });
      if (action === "reject") await api.post(`/admin/shops/${user.user_id}/reject`, { reason: note });
      if (action === "suspend") await api.post(`/admin/shops/${user.user_id}/suspend`, { reason: note });
      if (action === "reinstate") await api.post(`/admin/shops/${user.user_id}/reinstate`);
      toast.success(`Shop ${action}d`);
      setNote("");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121214] border-white/10 text-white rounded-none">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{titles.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-white/10 bg-[#09090B] p-3 text-sm">
            <div className="label-eyebrow text-zinc-500 mb-1">{user.role?.toUpperCase()}</div>
            <div className="font-medium">{user.shop_name || user.name}</div>
            <div className="text-xs text-zinc-500 font-mono">{user.email}</div>
          </div>
          <p className="text-sm text-zinc-400">{titles.body}</p>
          {action !== "reinstate" && (
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="input-base" placeholder={titles.placeholder} data-testid="action-note-input" />
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-secondary" data-testid="action-cancel">Cancel</button>
          <button onClick={submit} disabled={loading} className="btn-primary" data-testid="action-confirm">{loading ? "..." : titles.confirmText}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RoleBadge({ role }) {
  const colors = {
    admin: "bg-[#FF3B30] text-white",
    shop: "bg-[#007AFF] text-white",
    owner: "border border-white/10 text-zinc-400",
  };
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${colors[role] || "border border-white/10 text-zinc-400"}`}>{role || "pending"}</span>;
}

function StatusBadge({ status }) {
  if (!status) return <span className="text-[10px] uppercase tracking-widest px-2 py-1 border border-white/10 text-zinc-500">—</span>;
  const colors = {
    approved: "bg-[#34C759] text-black",
    pending: "bg-[#FF9500] text-black",
    rejected: "bg-[#FF3B30] text-white",
    suspended: "bg-[#FF3B30] text-white",
  };
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${colors[status] || "border border-white/10 text-zinc-500"}`}>{status}</span>;
}

function Th({ children }) { return <th className="px-4 py-3 label-eyebrow text-zinc-500">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }

function SubBucketCard({ title, sub, rows, accent = "text-white", icon: Icon, testid, onImpersonate, daysHint, headerAction }) {
  const empty = !rows || rows.length === 0;
  return (
    <div className="border border-white/10 bg-[#121214]" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {Icon && (
            <div className="w-8 h-8 bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center shrink-0">
              <Icon className="w-3.5 h-3.5 text-[#FF3B30]" strokeWidth={2.2} />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-heading text-lg font-bold tracking-tight truncate">{title}</div>
            {sub && <div className="text-[11px] text-zinc-500 font-mono mt-0.5 truncate">{sub}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {headerAction}
          <div className={`font-heading text-3xl font-black tracking-tighter ${accent}`}>{rows?.length ?? 0}</div>
        </div>
      </div>
      {empty ? (
        <div className="px-5 py-10 text-zinc-600 text-xs text-center">Nothing here yet.</div>
      ) : (
        <ul className="divide-y divide-white/5 max-h-72 overflow-y-auto" data-testid={`${testid}-list`}>
          {rows.map((r) => {
            const label = r.shop_name || r.name || "(unnamed shop)";
            // Compute a soft "X days left" or "paid X days ago" hint
            let hint = "";
            if (daysHint === "until" && r.premium_until) {
              const d = Math.ceil((new Date(r.premium_until).getTime() - Date.now()) / 86_400_000);
              hint = d >= 0 ? `${d}d left` : `expired ${Math.abs(d)}d ago`;
            } else if (daysHint === "paid" && r.last_paid_at) {
              const d = Math.floor((Date.now() - new Date(r.last_paid_at).getTime()) / 86_400_000);
              hint = `paid ${d}d ago`;
            } else if (daysHint === "approved" && r.approved_at) {
              const d = Math.floor((Date.now() - new Date(r.approved_at).getTime()) / 86_400_000);
              hint = `approved ${d}d ago`;
            }
            return (
              <li key={r.user_id} className="px-5 py-3 flex items-center gap-3 text-sm hover:bg-white/5 transition-colors" data-testid={`${testid}-row-${r.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{label}</div>
                  <div className="text-[11px] text-zinc-500 font-mono truncate">
                    {r.account_number ? `${r.account_number} · ` : ""}{r.email}
                  </div>
                </div>
                {hint && <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono shrink-0">{hint}</span>}
                <button
                  onClick={() => onImpersonate(r.user_id)}
                  className="text-zinc-500 hover:text-[#FF3B30] text-[10px] uppercase tracking-widest transition-colors shrink-0"
                  data-testid={`${testid}-impersonate-${r.user_id}`}
                  title="Impersonate"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SubscriptionsTab({ data, onImpersonate }) {
  const [sending, setSending] = useState(false);
  if (!data) return <div className="text-zinc-500 text-sm">Loading subscriptions…</div>;
  const t = data.totals || {};
  const conversion = t.approved_shops > 0 ? Math.round(((t.paid_premium || 0) / t.approved_shops) * 100) : 0;
  const neverIds = (data.never_signed_up || []).map((r) => r.user_id);
  const lastWinback = data.last_winback;
  const lastWinbackHint = (() => {
    if (!lastWinback?.at) return null;
    const days = Math.floor((Date.now() - new Date(lastWinback.at).getTime()) / 86_400_000);
    const when = days === 0 ? "today" : days === 1 ? "1 day ago" : `${days} days ago`;
    return `Last win-back: ${when} · ${lastWinback.sent} email${lastWinback.sent === 1 ? "" : "s"}`;
  })();

  const sendWinback = async () => {
    if (neverIds.length === 0) return;
    if (!window.confirm(`Send a win-back email to ${neverIds.length} shop${neverIds.length === 1 ? "" : "s"}? Shops already emailed in the last 14 days will be skipped.`)) return;
    setSending(true);
    try {
      const { data: res } = await api.post("/admin/shops/subscriptions/winback", { user_ids: neverIds });
      const parts = [`Sent: ${res.sent}`];
      if (res.skipped_cooldown) parts.push(`cooldown: ${res.skipped_cooldown}`);
      if (res.skipped_active) parts.push(`still active: ${res.skipped_active}`);
      if (res.skipped_paid) parts.push(`already paid: ${res.skipped_paid}`);
      toast.success(parts.join(" · "));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send win-back emails");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="subscriptions-tab">
      {/* Top KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
        <Stat label="ON TRIAL" value={t.on_trial} accent={t.on_trial > 0} icon={Gift} />
        <Stat label="PAID PREMIUM" value={t.paid_premium} accent={t.paid_premium > 0} icon={Crown} />
        <Stat label="FREE" value={t.free} icon={Users} />
        <Stat label="JUST UPGRADED" value={t.just_signed_up} accent={t.just_signed_up > 0} icon={TrendingUp} />
        <Stat label="NEVER UPGRADED" value={t.never_signed_up} icon={UserX} />
      </div>

      <div className="border border-white/10 bg-[#121214] px-5 py-4 flex items-center justify-between" data-testid="subs-conversion-bar">
        <div className="flex items-center gap-3">
          <CreditCard className="w-4 h-4 text-[#FF3B30]" />
          <div>
            <div className="text-sm font-semibold">Paid conversion</div>
            <div className="text-[11px] text-zinc-500 font-mono">{t.paid_premium || 0} of {t.approved_shops || 0} approved shops are on paid Premium</div>
          </div>
        </div>
        <div className="font-heading text-4xl font-black tracking-tighter">{conversion}<span className="text-base text-zinc-500 ml-1">%</span></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SubBucketCard
          title="Currently on trial"
          sub="Welcome trial is still active"
          rows={data.on_trial}
          accent="text-[#FF9500]"
          icon={Gift}
          testid="subs-on-trial"
          daysHint="until"
          onImpersonate={onImpersonate}
        />
        <SubBucketCard
          title="Paid Premium"
          sub="Stripe-paid, Premium active"
          rows={data.paid_premium}
          accent="text-[#FF3B30]"
          icon={Crown}
          testid="subs-paid-premium"
          daysHint="until"
          onImpersonate={onImpersonate}
        />
        <SubBucketCard
          title="Just signed up for Premium"
          sub="Paid within the last 30 days"
          rows={data.just_signed_up}
          accent="text-[#34C759]"
          icon={TrendingUp}
          testid="subs-just-signed-up"
          daysHint="paid"
          onImpersonate={onImpersonate}
        />
        <SubBucketCard
          title="Never signed up"
          sub={lastWinbackHint || "Trial ended, never paid"}
          rows={data.never_signed_up}
          accent="text-zinc-400"
          icon={UserX}
          testid="subs-never-signed-up"
          daysHint="approved"
          onImpersonate={onImpersonate}
          headerAction={(data.never_signed_up || []).length > 0 ? (
            <button
              type="button"
              onClick={sendWinback}
              disabled={sending}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FF3B30] disabled:opacity-50 transition-colors border border-white/10 hover:border-[#FF3B30]/40 px-2 py-1"
              data-testid="winback-email-btn"
              title="Send a friendly win-back email"
            >
              <Mail className="w-3 h-3" /> {sending ? "Sending…" : "Email these shops"}
            </button>
          ) : null}
        />
        <div className="lg:col-span-2">
          <SubBucketCard
            title="Free tier"
            sub="Trial expired or never granted, Premium inactive"
            rows={data.free}
            accent="text-zinc-300"
            icon={Users}
            testid="subs-free"
            daysHint="approved"
            onImpersonate={onImpersonate}
          />
        </div>
      </div>
    </div>
  );
}
